import { CheckIcon, AlertIcon, InfoIcon } from '@pipebase/ui';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ToastKind, ToastMessage } from '../types';


interface ToastApi {
  push: (kind: ToastKind, text: string) => void;
  toasts: ToastMessage[];
}

const ToastContext = createContext<ToastApi | null>(null);

const TOAST_DISMISS_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const push = useCallback((kind: ToastKind, text: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((prev) => [...prev, { id, kind, text }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_DISMISS_MS);
  }, []);

  const value = useMemo<ToastApi>(() => ({ push, toasts }), [push, toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

const kindStyles: Record<ToastKind, { bg: string; border: string; iconBg: string; iconColor: string; icon: React.ReactNode }> = {
  success: {
    bg: 'bg-white',
    border: 'border-ink-200',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    icon: <CheckIcon size={14} />,
  },
  error: {
    bg: 'bg-white',
    border: 'border-ink-200',
    iconBg: 'bg-red-50',
    iconColor: 'text-red-600',
    icon: <AlertIcon size={14} />,
  },
  info: {
    bg: 'bg-white',
    border: 'border-ink-200',
    iconBg: 'bg-brand-50',
    iconColor: 'text-brand-700',
    icon: <InfoIcon size={14} />,
  },
};

function ToastContainer({ toasts }: { toasts: ToastMessage[] }) {
  return (
    <div className="toast-container" role="status" aria-live="polite">
      {toasts.map((t) => {
        const s = kindStyles[t.kind];
        return (
          <div
            key={t.id}
            className={`flex items-start gap-2.5 max-w-sm rounded-lg border ${s.border} ${s.bg} px-3 py-2.5 shadow-soft animate-toast-in`}
            data-testid={`toast-${t.kind}`}
          >
            <span
              className={`mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full ${s.iconBg} ${s.iconColor}`}
              aria-hidden="true"
            >
              {s.icon}
            </span>
            <span className="text-sm text-ink-800 leading-5">{t.text}</span>
          </div>
        );
      })}
    </div>
  );
}
